import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useUser } from '../contexts/UserContext';
import { getSettingsItem } from '../components/settings/settings.registry';
import { SettingsList } from '../components/settings/SettingsList';
import { SettingsDetail } from '../components/settings/SettingsDetail';
import { PortalContainer } from '../components/portal/PortalContainer';

/**
 * Account Settings — routes between the settings list (/portal/settings) and a section detail
 * (/portal/settings/:section). Section content lives in self-contained components under
 * components/settings; this only owns the list/detail switch and legacy ?tab= handling.
 */
export function AccountSettings() {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const welcomeShownRef = useRef(false);

  // Legacy ?tab= links redirect to the nested detail route; welcome toast preserved.
  useEffect(() => {
    const tab = searchParams.get('tab');
    const welcome = searchParams.get('welcome');

    if (tab && !section && getSettingsItem(tab)) {
      navigate(`/portal/settings/${tab}${welcome ? '?welcome=true' : ''}`, {
        replace: true,
        state: location.state,
      });
      return;
    }

    // Guard against StrictMode's double-invoke (and any re-run) firing the toast twice.
    if (welcome === 'true' && location.state?.newUser && !welcomeShownRef.current) {
      welcomeShownRef.current = true;
      toast.success('Welcome! Complete your identity verification to start investing.', {
        duration: 5000,
        icon: '🎉',
      });
    }
  }, [searchParams, section, location.state, navigate]);

  const item = section ? getSettingsItem(section) : undefined;
  // Unknown section, or one hidden for this user (e.g. KYC once verified) → back to the list.
  const invalidDetail = !!section && (!item || !!item.isHidden?.(user));
  const isDetail = !!section && !invalidDetail;

  // Directional swap between the list and a section detail: opening a section slides it in from the
  // right; Back slides the list back in from the left. A direct/first load uses a gentle rise-in.
  const isFirstRef = useRef(true);
  const prevIsDetailRef = useRef(isDetail);
  let enterClass = 'rise-in';
  if (!isFirstRef.current) {
    if (isDetail && !prevIsDetailRef.current) enterClass = 'step-enter-fwd';
    else if (!isDetail && prevIsDetailRef.current) enterClass = 'step-enter-back';
  }
  useEffect(() => {
    isFirstRef.current = false;
    prevIsDetailRef.current = isDetail;
  }, [isDetail]);

  if (invalidDetail) return <Navigate to="/portal/settings" replace />;

  return (
    <PortalContainer>
      <div key={isDetail ? section : 'list'} className={enterClass}>
        {isDetail ? <SettingsDetail item={item!} /> : <SettingsList />}
      </div>
    </PortalContainer>
  );
}

export default AccountSettings;
